import React, { useState, useMemo } from 'react';
import type { AnyObjectSchema } from 'yup';

import { Space } from '@sravni/react-design-system/lib/Space';
import { Text } from '@sravni/react-design-system/lib/Typography';
import type { FormProps, GetOTPButtonProps, OTPInputProps } from '@sravni/react-silent-authentication-form';
import {
  Form,
  OTPInput,
  PhoneInput,
  NameInput,
  EmailInput,
  ServerErrorMessage,
  AgreementPersonalDataCheckbox,
  AgreementAdvertisingCheckbox,
  SubmitButton,
  GetOTPButton,
  useBehaviourState,
} from '@sravni/react-silent-authentication-form';
import { useBoolean } from '@sravni/react-utils';

const baseFieldsConfig = {
  name: NameInput,
  email: EmailInput,
  phone: PhoneInput,
};

type Props = FormProps & {
  classNameInputs?: string;
  fields?: string[];
  inputBg?: OTPInputProps['background'];
  btnText?: string;
  btnColor?: GetOTPButtonProps['color'];
  onSend?: () => void;

  additionalValidationSchema?: AnyObjectSchema;
  additionalValidationFields?: string[];

  customFieldsConfig?: Record<string, React.ComponentType>;
  customFieldProps?: Record<string, any>;
};

const AuthSilentForm: React.FC<Props> = (props) => {
  const {
    classNameInputs,
    fields,
    inputBg,
    btnText = 'Продолжить',
    btnColor = 'blue',
    onSend,

    additionalValidationSchema,
    additionalValidationFields,

    customFieldsConfig,
    customFieldProps,

    children,
    ...rest
  } = props;

  const fieldsConfig = useMemo(
    () => ({
      ...baseFieldsConfig,
      ...(customFieldsConfig ?? {}),
    }),
    [customFieldsConfig],
  );

  const [isCodeSent, setCodeSent] = useBoolean(false);
  const [isAgreementCheck, setIsAgreementCheck] = useState(true);
  const { isOTPSended, isConsentsReceived } = useBehaviourState();

  const handleSend = () => {
    onSend?.();
    setCodeSent.on();
  };

  const handleChangeAgreement = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsAgreementCheck(event.target.checked);
  };

  return (
    <Form {...rest} additionalValidationSchema={additionalValidationSchema}>
      <Space direction="vertical" size={12}>
        {isCodeSent && isOTPSended ? (
          <Space direction="vertical" size={8} justify="space-between">
            <Text size={14}>Мы отправили код на введенный номер</Text>

            <OTPInput background={inputBg} />

            <Text size={14} className="h-color-B100 h-cursor-pointer" onClick={setCodeSent.off}>
              Изменить номер?
            </Text>

            {isConsentsReceived === false && (
              <Space direction="vertical" size={8}>
                <AgreementPersonalDataCheckbox onChange={handleChangeAgreement} />
                {!isAgreementCheck && (
                  <Text size={14} className="h-color-O100">
                    Без этого согласия не сможем продолжить
                  </Text>
                )}
                <AgreementAdvertisingCheckbox />
              </Space>
            )}

            <SubmitButton color={btnColor}>Подтвердить</SubmitButton>
          </Space>
        ) : (
          <>
            <Space direction="vertical" size={12} className={classNameInputs}>
              {fields?.length ? (
                fields.map((field) => {
                  const Input = fieldsConfig[field];

                  if (!Input) {
                    return null;
                  }

                  const extraProps = customFieldProps?.[field] ?? {};

                  return <Input background={inputBg} key={field} required {...extraProps} />;
                })
              ) : (
                <PhoneInput background={inputBg} />
              )}
            </Space>

            {children}

            <GetOTPButton color={btnColor} onClick={handleSend} additionalValidationFields={additionalValidationFields}>
              {btnText}
            </GetOTPButton>
          </>
        )}
        <ServerErrorMessage />
      </Space>
    </Form>
  );
};

export default AuthSilentForm;
